import {useMessages} from "i18n";
import {NudgeRoot, UpPill} from "./UpgradeHints.styles";
import {ManageTransfersLink, PassLine} from "./ActiveStorageNotice.styles";

interface Props {
	/** The viewer's OWN active-storage cap, already formatted from the plan catalogue (e.g. "5 GB"). */
	capLabel: string;
	/** Opens the upgrade surface — the create flow never routes to `/pricing` (ui#217). */
	onUpgrade: () => void;
	/**
	 * What the Upgrade control says when it is not the plain upgrade route — today, the referred friend's
	 * gift (kitecorp/transferify-ui#1993). Null for the plain route. A LABEL for that one control, never a
	 * control of its own.
	 */
	ctaLabel?: string | null;
	/**
	 * A pass bought before the one-off stopped being sold pays for this send (kitecorp/transferify-ui#3046). The rule
	 * module decides it ({@link ../overLimit/overLimitOffer.storagePassPaid}); this view says so.
	 */
	passPaid?: boolean;
}

/**
 * The active-storage refusal strip (ui#1664): the api refused the send because the account's LIVE
 * transfers already fill the plan's cap — an account-level state no retry or file removal changes,
 * which is why it does not join the generic failure toast. Two exits, both real:
 *
 *  - delete an old transfer — a `/transfers` link. That destination is one of the CardSheets that
 *    COVER the persistent create panel rather than unmounting it, so the sender's draft (files,
 *    options, everything typed) survives the trip and the freed space can be used immediately;
 *  - upgrade — the compact dialog on the cheapest tier that lifts the cap (kitecorp/transferify-ui#3048).
 *    Opened in place, like every other upgrade affordance in the create flow (ui#217), because routing
 *    away is what WOULD destroy the draft.
 *
 * No pass is sold on it any more (kitecorp/transferify-ui#3916); one bought before then still pays for the send,
 * and the strip says so.
 *
 * `role="alert"`: the strip appears as the outcome of the submit the sender just pressed, replacing
 * a toast — it must be announced, not merely present. A dumb view otherwise: the refusal is decided
 * by `classifySenderFailure` (via the api's stable `errorId`), the cap is computed by the caller from the
 * plan catalogue, and the held pass is the rule module's answer.
 */
export function ActiveStorageNotice({capLabel, onUpgrade, ctaLabel = null, passPaid = false}: Props) {
	const {sender, common} = useMessages();
	const copy = sender.activeStorageFull;
	return (
		<NudgeRoot tabIndex={-1} role="alert">
			<span>
				{copy.body(capLabel)}
				{" "}
				<ManageTransfersLink to="/transfers">{copy.freeUpSpace}</ManageTransfersLink>
				{passPaid && <PassLine>{sender.overLimit.paid}</PassLine>}
			</span>
			<UpPill type="button" onClick={onUpgrade}>{ctaLabel ?? common.upgrade}</UpPill>
		</NudgeRoot>
	);
}
